import React, {useState, useMemo, useEffect, useRef} from 'react';
import {
    Button,
    Card,
    Col,
    DatePicker,
    message,
    Row,
    Table,
    Typography
} from 'antd';
import type {TableColumnsType} from 'antd';
import {
    CheckCircleFilled,
    ExclamationCircleFilled,
    LoadingOutlined
} from '@ant-design/icons';
import dayjs, {Dayjs} from 'dayjs';
import {useQuery, useMutation} from '@apollo/client';
import {t} from '../../../i18n';
import {useWarehouseContext} from '../../../contexts/WarehouseContext';
import {formatISODateTime} from '../../../utils/dateTimeUtils';
import {
    GET_PENDING_PUSH_ORDERS,
    GetPendingPushOrdersResponse,
    GetPendingPushOrdersVariables,
    PendingPushBatchSummary
} from './graphql/getPendingPushOrders';
import {
    LIST_DATES_WITH_PUSH_TASK,
    ListDatesWithPushTaskResponse,
    ListDatesWithPushTaskVariables
} from './graphql/listDatesWithPushTask';
import {
    LIST_AREA_PENDING_PUSH_ORDERS,
    ListAreaPendingPushOrdersResponse,
    ListAreaPendingPushOrdersVariables,
    AreaPendingPushSummary
} from './graphql/listAreaPendingPushOrders';
import {
    PUSH_DSP_TASK,
    PushDspTaskVariables
} from './graphql/pushDspTask';

const {Text} = Typography;

type Step = 1 | 2 | 3 | 4;
type PushStatus = 'idle' | 'loading' | 'success' | 'error';

const stepTitles: {step: Step; labelKey: string}[] = [
    {step: 1, labelKey: 'delivery.routesNew.label.step1Title'},
    {step: 2, labelKey: 'delivery.routesNew.label.step2Title'},
    {step: 3, labelKey: 'delivery.routesNew.label.step3Title'},
    {step: 4, labelKey: 'delivery.routesNew.label.step4Title'}
];

/** 推送状态枚举码 -> i18n key（与后端 BatchPushStatus 一致：10/15/20） */
const PUSH_STATUS_KEYS: Record<number, string> = {
    10: 'PENDING_PUSH',
    15: 'PARTIAL_PUSH',
    20: 'PUSHED'
};

/** WMS 作业批次状态枚举码 -> i18n key（与后端 WmsBatchStatus 一致：0=未知） */
const WMS_BATCH_STATUS_KEYS: Record<number, string> = {
    0: 'UNKNOWN'
};

const formatBatchPushStatus = (status: number | null): string => {
    if (status == null) return '-';
    const key = PUSH_STATUS_KEYS[status];
    if (!key) return String(status);
    return t(`delivery.routesNew.enums.batchPushStatus.${key}`, {defaultValue: key});
};

const formatWmsBatchStatus = (status: number | null): string => {
    if (status == null) return '-';
    const key = WMS_BATCH_STATUS_KEYS[status];
    if (!key) return String(status);
    return t(`delivery.routesNew.enums.wmsBatchStatus.${key}`, {defaultValue: key});
};

const RoutesNewPage: React.FC = () => {
    const {currentWarehouse} = useWarehouseContext();
    const warehouseId = Number(currentWarehouse?.id) || 0;

    const [currentStep, setCurrentStep] = useState<Step>(1);
    const [selectedBatchNumbers, setSelectedBatchNumbers] = useState<Set<string>>(new Set());
    const [selectedTaskDate, setSelectedTaskDate] = useState<Dayjs>(dayjs());
    const [pushedDates, setPushedDates] = useState<Set<string>>(new Set());
    const [selectedAreaIds, setSelectedAreaIds] = useState<Set<number>>(new Set());
    const [areaList, setAreaList] = useState<AreaPendingPushSummary[]>([]);
    const [lastPushParams, setLastPushParams] = useState<{
        batchNumbers: string[];
        areaIds: number[];
        taskDate: string;
        warehouseId: number;
    } | null>(null);
    const [pushStatus, setPushStatus] = useState<PushStatus>('idle');
    const hasInitializedBatchSelection = useRef(false);

    const today = dayjs();
    const tomorrow = today.add(1, 'day');

    const {data: pendingData, loading: pendingLoading, error: pendingError} = useQuery<
        GetPendingPushOrdersResponse,
        GetPendingPushOrdersVariables
    >(GET_PENDING_PUSH_ORDERS, {
        variables: {warehouseId},
        skip: !warehouseId
    });

    const pendingSummary = pendingData?.queryPendingPushOrders ?? null;
    const batchList = useMemo(() => {
        const list = pendingSummary?.batchList ?? [];
        const returnRedispatch = list.filter(
            (b) =>
                b.wmsBatchNumber?.toUpperCase().includes('RETURN') ||
                b.wmsBatchNumber?.toUpperCase().includes('REDISPATCH')
        );
        const normal = list.filter(
            (b) =>
                !b.wmsBatchNumber?.toUpperCase().includes('RETURN') &&
                !b.wmsBatchNumber?.toUpperCase().includes('REDISPATCH')
        );
        return [...normal, ...returnRedispatch];
    }, [pendingSummary?.batchList]);

    const dateRangeStart = selectedTaskDate.startOf('month').format('YYYY-MM-DD');
    const dateRangeEnd = selectedTaskDate.add(1, 'day').format('YYYY-MM-DD');

    const {data: datesData} = useQuery<
        ListDatesWithPushTaskResponse,
        ListDatesWithPushTaskVariables
    >(LIST_DATES_WITH_PUSH_TASK, {
        variables: {
            startDate: dateRangeStart,
            endDate: dateRangeEnd,
            warehouseId
        },
        skip: !warehouseId || currentStep < 2
    });

    useEffect(() => {
        if (datesData?.listDatesWithPushTask) {
            setPushedDates(new Set(datesData.listDatesWithPushTask));
        }
    }, [datesData]);

    const {data: areaData, loading: areaLoading} = useQuery<
        ListAreaPendingPushOrdersResponse,
        ListAreaPendingPushOrdersVariables
    >(LIST_AREA_PENDING_PUSH_ORDERS, {
        variables: {
            batchNumbers: Array.from(selectedBatchNumbers),
            warehouseId
        },
        skip: !warehouseId || selectedBatchNumbers.size === 0 || currentStep !== 3
    });

    useEffect(() => {
        if (areaData?.listAreaPendingPushOrders) {
            setAreaList(areaData.listAreaPendingPushOrders);
        }
    }, [areaData]);

    useEffect(() => {
        hasInitializedBatchSelection.current = false;
    }, [warehouseId]);

    useEffect(() => {
        if (batchList.length === 0 || hasInitializedBatchSelection.current) return;
        hasInitializedBatchSelection.current = true;
        const returnRedispatchNumbers = batchList
            .filter((b) => {
                if (!b.wmsBatchNumber || (b.pushableCount ?? 0) <= 0) return false;
                const num = b.wmsBatchNumber.toUpperCase();
                return (
                    num === 'RETURN_REDISPATCH_BATCH' ||
                    num.includes('RETURN') ||
                    num.includes('REDISPATCH')
                );
            })
            .map((b) => b.wmsBatchNumber as string);
        if (returnRedispatchNumbers.length > 0) {
            setSelectedBatchNumbers((prev) => new Set([...prev, ...returnRedispatchNumbers]));
        }
    }, [batchList]);

    const [pushDspTaskMutation] = useMutation<{pushDspTask: boolean}, PushDspTaskVariables>(
        PUSH_DSP_TASK
    );

    const selectedBatchesDetail = useMemo(() => {
        if (!batchList.length || selectedBatchNumbers.size === 0) {
            return {
                batchCount: 0,
                pushableParcels: 0,
                returnRedispatchParcels: 0
            };
        }
        const selected = batchList.filter((b) =>
            b.wmsBatchNumber ? selectedBatchNumbers.has(b.wmsBatchNumber) : false
        );
        const isReturnRedispatch = (b: PendingPushBatchSummary) => {
            if (!b.wmsBatchNumber) return false;
            const num = b.wmsBatchNumber.toUpperCase();
            return (
                num === 'RETURN_REDISPATCH_BATCH' ||
                num.includes('RETURN') ||
                num.includes('REDISPATCH')
            );
        };
        const returnBatches = selected.filter(isReturnRedispatch);
        const normalBatches = selected.filter((b) => !isReturnRedispatch(b));
        const pushableParcels = normalBatches.reduce((s, b) => s + (b.pushableCount ?? 0), 0);
        const returnRedispatchParcels = returnBatches.reduce(
            (s, b) => s + (b.pushableCount ?? 0),
            0
        );
        return {
            batchCount: normalBatches.length,
            pushableParcels,
            returnRedispatchParcels
        };
    }, [batchList, selectedBatchNumbers]);

    const overviewStep1 = {
        card1: pendingSummary?.newArrivalBatchCount ?? 0,
        card2: pendingSummary?.newArrivalPushableParcelCount ?? 0,
        card3: pendingSummary?.returnRedispatchParcelCount ?? 0
    };

    const overviewStep2And3 = {
        card1: selectedBatchesDetail.batchCount,
        card2: selectedBatchesDetail.pushableParcels,
        card3: selectedBatchesDetail.returnRedispatchParcels
    };

    const totalPendingParcels = useMemo(() => {
        return areaList
            .filter((a) => a.areaId != null && selectedAreaIds.has(a.areaId))
            .reduce((s, a) => s + (a.pendingPushCount ?? 0), 0);
    }, [areaList, selectedAreaIds]);

    const isTaskDatePushed = useMemo(() => {
        const key = selectedTaskDate.format('YYYY-MM-DD');
        return pushedDates.has(key);
    }, [selectedTaskDate, pushedDates]);

    const renderStepStatus = (step: Step) => {
        if (step < currentStep) return 'done';
        if (step === currentStep) return 'current';
        return 'pending';
    };

    const renderStepCircle = (step: Step) => {
        const status = renderStepStatus(step);
        if (status === 'done') {
            return (
                <div className="flex items-center justify-center w-7 h-7 rounded-full bg-green-500 text-white text-xs">
                    ✓
                </div>
            );
        }
        const isCurrent = status === 'current';
        const circleClasses = isCurrent
            ? 'bg-indigo-600 text-white'
            : 'bg-gray-300 text-gray-600';
        return (
            <div
                className={`flex items-center justify-center w-7 h-7 rounded-full text-xs ${circleClasses}`}
            >
                {step}
            </div>
        );
    };

    const renderSteps = () => (
        <div className="flex items-center justify-between mb-6">
            {stepTitles.map((item, index) => {
                const isLast = index === stepTitles.length - 1;
                const status = renderStepStatus(item.step);
                const labelColor =
                    status === 'current' ? '#4b0082' : status === 'done' ? '#52c41a' : '#8c8c8c';
                return (
                    <React.Fragment key={item.step}>
                        <div className="flex items-center">
                            {renderStepCircle(item.step)}
                            <span
                                style={{marginLeft: 8, color: labelColor, fontSize: 14}}
                            >
                                {t(item.labelKey)}
                            </span>
                        </div>
                        {!isLast && (
                            <div
                                className="flex-1 h-px mx-4"
                                style={{backgroundColor: '#d9d9d9'}}
                            />
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );

    const renderOverview = () => {
        if (currentStep === 4) return null;
        const isStep1 = currentStep === 1;
        const card1 = isStep1 ? overviewStep1.card1 : overviewStep2And3.card1;
        const card2 = isStep1 ? overviewStep1.card2 : overviewStep2And3.card2;
        const card3 = isStep1 ? overviewStep1.card3 : overviewStep2And3.card3;
        const bgClass = isStep1 ? 'bg-blue-50' : 'bg-green-50';
        const borderClass = 'border border-blue-200';
        const batchTitleKey = isStep1
            ? 'delivery.routesNew.label.overviewBatchSelectable'
            : 'delivery.routesNew.label.overviewBatchSelected';

        return (
            <Row gutter={16} className="mb-6">
                <Col span={8}>
                    <div className={`rounded p-4 ${borderClass} ${bgClass} text-center`}>
                        <div className="text-gray-500 text-sm mb-1 flex items-center justify-center">
                            {currentStep >= 2 && (
                                <span className="inline-flex items-center mr-1">
                                    <span className="flex items-center justify-center w-4 h-4 rounded-full bg-green-500 text-white text-[10px] mr-1">
                                        ✓
                                    </span>
                                </span>
                            )}
                            <span>{t(batchTitleKey)}</span>
                        </div>
                        <div className="text-black font-medium text-center">{card1}</div>
                    </div>
                </Col>
                <Col span={8}>
                    <div className={`rounded p-4 ${borderClass} ${bgClass} text-center`}>
                        <div className="text-gray-500 text-sm mb-1 flex items-center justify-center">
                            {currentStep >= 2 && (
                                <span className="inline-flex items-center mr-1">
                                    <span className="flex items-center justify-center w-4 h-4 rounded-full bg-green-500 text-white text-[10px] mr-1">
                                        ✓
                                    </span>
                                </span>
                            )}
                            <span>
                                {isStep1
                                    ? t('delivery.routesNew.label.overviewPushableParcels')
                                    : t('delivery.routesNew.label.overviewPushableParcelsSelected')}
                            </span>
                        </div>
                        <div className="text-blue-600 font-medium text-center">{card2}</div>
                    </div>
                </Col>
                <Col span={8}>
                    <div className="rounded p-4 bg-gray-50 border border-gray-200 text-center">
                        <div className="text-gray-500 text-sm mb-1">
                            {t('delivery.routesNew.label.overviewReturnRedispatch')}
                        </div>
                        <div className="text-blue-600 font-medium text-center">{card3}</div>
                    </div>
                </Col>
            </Row>
        );
    };

    const handleConfirmBatch = () => {
        if (selectedBatchNumbers.size === 0) {
            message.warning(t('delivery.routesNew.message.selectAtLeastOneBatch'));
            return;
        }
        if (selectedBatchesDetail.pushableParcels <= 0) {
            message.warning(t('delivery.routesNew.message.selectedBatchHasNoPushableParcels'));
            return;
        }
        setCurrentStep(2);
    };

    const handleBackToStep1 = () => setCurrentStep(1);
    const handleConfirmTaskDate = () => {
        setCurrentStep(3);
    };

    const handleBackToStep2 = () => setCurrentStep(2);

    const handlePush = async () => {
        if (selectedAreaIds.size === 0) {
            message.warning(t('delivery.routesNew.message.selectAtLeastOneArea'));
            return;
        }
        const params = {
            batchNumbers: Array.from(selectedBatchNumbers),
            areaIds: Array.from(selectedAreaIds),
            taskDate: selectedTaskDate.format('YYYY-MM-DD'),
            warehouseId
        };
        setLastPushParams(params);
        setPushStatus('loading');
        setCurrentStep(4);
        try {
            const result = await pushDspTaskMutation({
                variables: {
                    request: {
                        batchNumbers: params.batchNumbers,
                        areaIds: params.areaIds,
                        taskDate: params.taskDate,
                        warehouseId: params.warehouseId
                    }
                }
            });
            if (result.data?.pushDspTask) {
                setPushStatus('success');
            } else {
                setPushStatus('error');
            }
        } catch {
            setPushStatus('error');
        }
    };

    const handleRetryPush = async () => {
        if (!lastPushParams) return;
        setPushStatus('loading');
        try {
            const result = await pushDspTaskMutation({
                variables: {
                    request: {
                        batchNumbers: lastPushParams.batchNumbers,
                        areaIds: lastPushParams.areaIds,
                        taskDate: lastPushParams.taskDate,
                        warehouseId: lastPushParams.warehouseId
                    }
                }
            });
            if (result.data?.pushDspTask) {
                setPushStatus('success');
                message.success(t('delivery.routesNew.message.pushSuccess'));
            } else {
                setPushStatus('error');
            }
        } catch {
            setPushStatus('error');
        }
    };

    const handleBackToStep3 = () => {
        setPushStatus('idle');
        setCurrentStep(3);
    };

    const batchColumns: TableColumnsType<PendingPushBatchSummary> = [
        {
            title: t('delivery.routesNew.label.columnBatchNumber'),
            dataIndex: 'wmsBatchNumber',
            key: 'wmsBatchNumber',
            render: (v: string | null) => v ?? '-'
        },
        {
            title: t('delivery.routesNew.label.columnPushStatus'),
            dataIndex: 'pushStatus',
            key: 'pushStatus',
            render: (v: number | null) => formatBatchPushStatus(v)
        },
        {
            title: t('delivery.routesNew.label.columnBatchStatus'),
            dataIndex: 'wmsBatchStatus',
            key: 'wmsBatchStatus',
            render: (v: number | null) => formatWmsBatchStatus(v)
        },
        {
            title: t('delivery.routesNew.label.columnPushableCount'),
            dataIndex: 'pushableCount',
            key: 'pushableCount',
            render: (v: number) => (v != null ? String(v) : '-')
        },
        {
            title: t('delivery.routesNew.label.columnSendHub'),
            dataIndex: 'sendHubName',
            key: 'sendHubName',
            render: (v: string) => v ?? '-'
        },
        {
            title: t('delivery.routesNew.label.columnPlanWhsArrivalTime'),
            dataIndex: 'planWhsArrivalTime',
            key: 'planWhsArrivalTime',
            render: (v: string) => (v ? formatISODateTime(v) : '-')
        },
        {
            title: t('delivery.routesNew.label.columnWhsArrivalTime'),
            dataIndex: 'whsArrivalTime',
            key: 'whsArrivalTime',
            render: (v: string) => (v ? formatISODateTime(v) : '-')
        }
    ];

    const areaColumns: TableColumnsType<AreaPendingPushSummary> = [
        {
            title: t('delivery.routesNew.label.columnAreaName'),
            dataIndex: 'areaName',
            key: 'areaName',
            render: (v: string) => v ?? '-'
        },
        {
            title: t('delivery.routesNew.label.columnPendingPushCount'),
            dataIndex: 'pendingPushCount',
            key: 'pendingPushCount',
            render: (v: number) => (v != null ? String(v) : '-')
        },
        {
            title: t('delivery.routesNew.label.columnNewArrivalCount'),
            dataIndex: 'newArrivalCount',
            key: 'newArrivalCount',
            render: (v: number) => (v != null ? String(v) : '-')
        },
        {
            title: t('delivery.routesNew.label.columnReturnRedispatchCount'),
            dataIndex: 'returnRedispatchCount',
            key: 'returnRedispatchCount',
            render: (v: number) => (v != null ? String(v) : '-')
        },
        {
            title: t('delivery.routesNew.label.columnPushedCount'),
            dataIndex: 'pushedCount',
            key: 'pushedCount',
            render: (v: number) => (v != null ? String(v) : '-')
        },
        {
            title: t('delivery.routesNew.label.columnDspName'),
            dataIndex: 'dspName',
            key: 'dspName',
            render: (v: string) => v ?? '-'
        }
    ];

    const rowBatchSelection = {
        selectedRowKeys: Array.from(selectedBatchNumbers),
        onChange: (keys: React.Key[]) => {
            setSelectedBatchNumbers(new Set(keys as string[]));
        },
        getCheckboxProps: (record: PendingPushBatchSummary) => ({
            disabled: (record.pushableCount ?? 0) <= 0
        })
    };

    const rowAreaSelection = {
        selectedRowKeys: Array.from(selectedAreaIds),
        onChange: (keys: React.Key[]) => {
            setSelectedAreaIds(new Set(keys as number[]));
        },
        getCheckboxProps: (record: AreaPendingPushSummary) => ({
            disabled: (record.pendingPushCount ?? 0) <= 0
        })
    };

    const renderStepContent = () => {
        if (currentStep === 1) {
            return (
                <Card>
                    <div className="flex justify-between items-start mb-4">
                        <Text strong>{t('delivery.routesNew.label.step1Description')}</Text>
                        <Button
                            type="primary"
                            className="bg-indigo-600"
                            onClick={handleConfirmBatch}
                        >
                            {t('delivery.routesNew.button.confirmBatch')}
                        </Button>
                    </div>
                    {!warehouseId ? (
                        <Text type="secondary">{t('delivery.routesNew.message.selectWarehouse')}</Text>
                    ) : pendingError ? (
                        <div className="py-4">
                            <Text type="danger">{t('delivery.routesNew.message.loadBatchFailed')}</Text>
                            <div className="text-gray-500 text-sm mt-1">{pendingError.message}</div>
                        </div>
                    ) : pendingLoading ? (
                        <div className="py-8 text-center text-gray-500">
                            <LoadingOutlined /> {t('common.messages.loading')}
                        </div>
                    ) : !batchList.length ? (
                        <Text type="secondary">{t('delivery.routesNew.message.noBatchData')}</Text>
                    ) : (
                        <Table
                            rowKey="wmsBatchNumber"
                            rowClassName={(record: PendingPushBatchSummary) =>
                                (record.pushableCount ?? 0) <= 0 ? 'text-gray-400 bg-gray-50' : ''
                            }
                            columns={batchColumns}
                            dataSource={batchList}
                            rowSelection={rowBatchSelection}
                            pagination={false}
                            scroll={{x: 800}}
                        />
                    )}
                </Card>
            );
        }

        if (currentStep === 2) {
            return (
                <Card>
                    <div className="mb-4 flex items-start gap-4">
                        <div className="p-3 bg-orange-50 border border-orange-200 rounded flex items-start gap-2 max-w-xl">
                            <ExclamationCircleFilled className="text-red-500 text-lg flex-shrink-0 mt-0.5" />
                            <div>
                                <Text className="text-orange-800">
                                    {t('delivery.routesNew.message.step2Warning')}
                                </Text>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <Button onClick={handleBackToStep1}>
                                {t('delivery.routesNew.button.backToBatch')}
                            </Button>
                            <Button
                                type="primary"
                                className="bg-indigo-600"
                                onClick={handleConfirmTaskDate}
                            >
                                {t('delivery.routesNew.button.confirmTaskDate')}
                            </Button>
                        </div>
                    </div>
                    <div className="mb-2">
                        <Text strong>
                            <Text type="danger">* </Text>
                            {t('delivery.routesNew.label.step2Title')}：{t('delivery.routesNew.label.step2SelectDate')}
                        </Text>
                    </div>
                    <div className="mb-4">
                        <DatePicker
                            value={selectedTaskDate}
                            onChange={(d) => d && setSelectedTaskDate(d)}
                            format="YYYY-MM-DD"
                            disabledDate={(current) => {
                                if (!current) return true;
                                return current.isBefore(today, 'day') || current.isAfter(tomorrow, 'day');
                            }}
                        />
                    </div>
                    {isTaskDatePushed && (
                        <Text type="danger" className="text-sm block mb-4">
                            {t('delivery.routesNew.message.taskDateAlreadyPushed')}
                        </Text>
                    )}
                </Card>
            );
        }

        if (currentStep === 3) {
            return (
                <Card>
                    <div className="flex justify-between items-start flex-wrap gap-2 mb-2">
                        <Text strong>{t('delivery.routesNew.label.step3Description')}</Text>
                        <Text type="secondary">
                            {t('delivery.routesNew.label.taskDatePrefix')}
                            {selectedTaskDate.format('YYYY-MM-DD')}
                        </Text>
                    </div>
                    <div className="text-gray-500 text-sm mb-2">
                        {t('delivery.routesNew.message.selectedAreasSummary', {
                            areaCount: selectedAreaIds.size,
                            parcelCount: totalPendingParcels
                        })}
                    </div>
                    <div className="flex justify-end gap-2 mb-4">
                        <Button onClick={handleBackToStep2}>
                            {t('delivery.routesNew.button.backToTaskDate')}
                        </Button>
                        <Button
                            type="primary"
                            className="bg-indigo-600"
                            onClick={handlePush}
                        >
                            {t('delivery.routesNew.button.confirmPush')}
                        </Button>
                    </div>
                    {areaLoading ? (
                        <div className="py-8 text-center text-gray-500">
                            <LoadingOutlined /> {t('common.messages.loading')}
                        </div>
                    ) : !areaList.length ? (
                        <Text type="secondary">{t('delivery.routesNew.message.noAreaData')}</Text>
                    ) : (
                        <Table
                            rowKey="areaId"
                            rowClassName={(record: AreaPendingPushSummary) =>
                                (record.pendingPushCount ?? 0) <= 0 ? 'text-gray-400 bg-gray-50' : ''
                            }
                            columns={areaColumns}
                            dataSource={areaList}
                            rowSelection={rowAreaSelection}
                            pagination={false}
                            scroll={{x: 800}}
                        />
                    )}
                </Card>
            );
        }

        if (currentStep === 4) {
            if (pushStatus === 'loading') {
                return (
                    <div className="border border-blue-200 bg-blue-50 rounded p-4 flex items-center gap-3">
                        <LoadingOutlined className="text-blue-600 text-2xl" />
                        <Text>{t('delivery.routesNew.message.pushing')}</Text>
                    </div>
                );
            }
            if (pushStatus === 'success') {
                return (
                    <div className="border border-green-200 bg-green-50 rounded p-4 flex items-start gap-3">
                        <CheckCircleFilled className="text-green-500 text-2xl flex-shrink-0" />
                        <div>
                            <Text strong className="block mb-1">
                                {t('delivery.routesNew.message.pushSuccessTitle')}
                            </Text>
                            <Text type="secondary" className="text-sm">
                                {t('delivery.routesNew.message.pushHistoryLink')}{' '}
                                <a
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        window.location.href = '/wms/routes-history';
                                    }}
                                >
                                    {t('delivery.routesNew.label.pushHistoryPage')}
                                </a>
                            </Text>
                        </div>
                    </div>
                );
            }
            if (pushStatus === 'error') {
                return (
                    <div className="border border-red-200 bg-red-50 rounded p-4 flex items-start gap-3">
                        <ExclamationCircleFilled className="text-red-500 text-2xl flex-shrink-0" />
                        <div className="flex-1">
                            <Text strong className="block mb-2">
                                {t('delivery.routesNew.message.pushFailedTitle')}
                            </Text>
                            <div className="flex gap-2">
                                <Button danger onClick={handleRetryPush}>
                                    {t('delivery.routesNew.button.retryPush')}
                                </Button>
                                <Button onClick={handleBackToStep3}>
                                    {t('delivery.routesNew.button.backToModify')}
                                </Button>
                            </div>
                        </div>
                    </div>
                );
            }
        }

        return null;
    };

    return (
        <div style={{padding: 24}}>
            <Row justify="space-between" align="middle" style={{marginBottom: 16}}>
                <Col>
                    <div className="flex items-center space-x-4">
                        <div className="border-l-4 border-indigo-600 pl-2 text-xl font-medium">
                            {t('delivery.routesNew.label.taskPush')}
                        </div>
                    </div>
                </Col>
            </Row>

            {renderSteps()}
            {renderOverview()}
            {renderStepContent()}
        </div>
    );
};

export default RoutesNewPage;
